/**
 * Formulario de configuración de Poste
 * Permite configurar herrajes, tensores, cables y reservorios/gasas
 * @module components/forms/PosteConfigForm
 */

import { useState } from 'react'
import { Plus, Trash2, Cable, Box, Anchor, AlertTriangle, Package, Archive } from 'lucide-react'

// Tipos
type TipoPoste = 'madera' | 'concreto' | 'metal'
type PropietarioPoste = 'propia' | 'alquilado' | 'compartido'
type EstadoFisico = 'bueno' | 'regular' | 'malo'
type TipoHerraje = 'grapa' | 'abrazadera' | 'gancho' | 'fleje' | 'preformado'
type TipoTensor = 'tensor' | 'rienda' | 'viento' | 'ancla'
type TipoInstalacion = 'aereo' | 'bajante' | 'subida'

interface Herraje {
  id: string
  tipo: TipoHerraje
  cantidad: number
  material: string
  notas?: string
}

interface Tensor {
  id: string
  tipo: TipoTensor
  longitud: number
  angulo?: number
  notas?: string
}

interface CableEnPoste {
  id: string
  cableId?: string
  cableNombre: string
  tipoInstalacion: TipoInstalacion
  altura: number
  reserva: number // metros de reserva/gasa
  tieneGasa: boolean
  metrosEnPoste: number
  notas?: string
}

type TipoReserva = 'bobina' | 'figura8' | 'serpentin' | 'caja'
type PropositoReserva = 'mantenimiento' | 'extension' | 'reubicacion' | 'emergencia'

interface ReservaFibra {
  id: string
  cableId?: string
  cableNombre: string
  metrosReserva: number
  tipoReserva: TipoReserva
  proposito: PropositoReserva
  alturaInstalacion: number
  condicion: 'buena' | 'regular' | 'mala'
  fechaInstalacion?: string
  notas?: string
}

interface PosteConfig {
  codigo?: string
  tipoPoste: TipoPoste
  altura: number
  propietario: PropietarioPoste
  empresa?: string
  herrajes: Herraje[]
  tensores: Tensor[]
  cables: CableEnPoste[]
  reservas: ReservaFibra[]
  estadoFisico: EstadoFisico
  requiereMantenimiento: boolean
  observaciones?: string
}

interface PosteConfigFormProps {
  config?: PosteConfig
  onChange: (config: PosteConfig) => void
  cablesDisponibles?: Array<{ id: string; nombre: string }>
}

const TIPOS_POSTE: Array<{ value: TipoPoste; label: string }> = [
  { value: 'concreto', label: 'Concreto' },
  { value: 'madera', label: 'Madera' },
  { value: 'metal', label: 'Metal/Acero' },
]

const ALTURAS_COMUNES = [8, 9, 10, 11, 12, 14, 15]

export function PosteConfigForm({ config, onChange, cablesDisponibles = [] }: PosteConfigFormProps) {
  const getInitialConfig = (): PosteConfig => ({
    codigo: config?.codigo,
    tipoPoste: config?.tipoPoste || 'concreto',
    altura: config?.altura || 10,
    propietario: config?.propietario || 'alquilado',
    empresa: config?.empresa,
    herrajes: config?.herrajes || [],
    tensores: config?.tensores || [],
    cables: config?.cables || [],
    reservas: config?.reservas || [],
    estadoFisico: config?.estadoFisico || 'bueno',
    requiereMantenimiento: config?.requiereMantenimiento || false,
    observaciones: config?.observaciones,
  })
  
  const [localConfig, setLocalConfig] = useState<PosteConfig>(getInitialConfig())

  const updateConfig = (updates: Partial<PosteConfig>) => {
    const newConfig = { ...localConfig, ...updates }
    setLocalConfig(newConfig)
    onChange(newConfig)
  }

  // Herrajes
  const addHerraje = () => {
    const nuevoHerraje: Herraje = {
      id: `HRJ-${Date.now()}`,
      tipo: 'grapa',
      cantidad: 1,
      material: 'Acero galvanizado',
    }
    updateConfig({ herrajes: [...localConfig.herrajes, nuevoHerraje] })
  }

  const removeHerraje = (id: string) => {
    updateConfig({ herrajes: localConfig.herrajes.filter(h => h.id !== id) })
  }

  const updateHerraje = (id: string, updates: Partial<Herraje>) => {
    updateConfig({
      herrajes: localConfig.herrajes.map(h => h.id === id ? { ...h, ...updates } : h)
    })
  }

  // Tensores
  const addTensor = () => {
    const nuevoTensor: Tensor = {
      id: `TNS-${Date.now()}`,
      tipo: 'tensor',
      longitud: 5,
    }
    updateConfig({ tensores: [...localConfig.tensores, nuevoTensor] })
  }

  const removeTensor = (id: string) => {
    updateConfig({ tensores: localConfig.tensores.filter(t => t.id !== id) })
  }

  const updateTensor = (id: string, updates: Partial<Tensor>) => {
    updateConfig({
      tensores: localConfig.tensores.map(t => t.id === id ? { ...t, ...updates } : t)
    })
  }

  // Cables
  const addCable = () => {
    const nuevoCable: CableEnPoste = {
      id: `CBL-${Date.now()}`,
      cableNombre: '',
      tipoInstalacion: 'aereo',
      altura: localConfig.altura - 1,
      reserva: 0,
      tieneGasa: false,
      metrosEnPoste: 0,
    }
    updateConfig({ cables: [...localConfig.cables, nuevoCable] })
  }

  const removeCable = (id: string) => {
    updateConfig({ cables: localConfig.cables.filter(c => c.id !== id) })
  }

  const updateCable = (id: string, updates: Partial<CableEnPoste>) => {
    updateConfig({
      cables: localConfig.cables.map(c => c.id === id ? { ...c, ...updates } : c)
    })
  }

  // Reservas de fibra
  const addReserva = () => {
    const nuevaReserva: ReservaFibra = {
      id: `RSV-${Date.now()}`,
      cableNombre: '',
      metrosReserva: 5,
      tipoReserva: 'bobina',
      proposito: 'mantenimiento',
      alturaInstalacion: localConfig.altura - 2,
      condicion: 'buena',
    }
    updateConfig({ reservas: [...localConfig.reservas, nuevaReserva] })
  }

  const removeReserva = (id: string) => {
    updateConfig({ reservas: localConfig.reservas.filter(r => r.id !== id) })
  }

  const updateReserva = (id: string, updates: Partial<ReservaFibra>) => {
    updateConfig({
      reservas: localConfig.reservas.map(r => r.id === id ? { ...r, ...updates } : r)
    })
  }

  const totalReserva = localConfig.cables.reduce((acc, c) => acc + c.reserva, 0) + 
                       localConfig.reservas.reduce((acc, r) => acc + r.metrosReserva, 0)
  const cablesConGasa = localConfig.cables.filter(c => c.tieneGasa).length

  return (
    <div className="space-y-6">
      {/* Información básica del poste */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Código del Poste</label>
          <input
            type="text"
            value={localConfig.codigo || ''}
            onChange={(e) => updateConfig({ codigo: e.target.value })}
            placeholder="Ej: P-001, CFE-12345"
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Tipo de Poste</label>
          <select
            value={localConfig.tipoPoste}
            onChange={(e) => updateConfig({ tipoPoste: e.target.value as TipoPoste })}
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          >
            {TIPOS_POSTE.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Altura (metros)</label>
          <select
            value={localConfig.altura}
            onChange={(e) => updateConfig({ altura: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          >
            {ALTURAS_COMUNES.map(h => (
              <option key={h} value={h}>{h} m</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Propietario</label>
          <select
            value={localConfig.propietario}
            onChange={(e) => updateConfig({ propietario: e.target.value as PropietarioPoste })}
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          >
            <option value="propia">Propio</option>
            <option value="alquilado">Alquilado/Rentado</option>
            <option value="compartido">Compartido</option>
          </select>
        </div>
        {localConfig.propietario !== 'propia' && (
          <div>
            <label className="block text-sm font-medium mb-1">Empresa Propietaria</label>
            <input
              type="text"
              value={localConfig.empresa || ''}
              onChange={(e) => updateConfig({ empresa: e.target.value })}
              placeholder="Ej: CFE, Telmex"
              className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
            />
          </div>
        )}
        <div>
          <label className="block text-sm font-medium mb-1">Estado Físico</label>
          <select
            value={localConfig.estadoFisico}
            onChange={(e) => updateConfig({ estadoFisico: e.target.value as EstadoFisico })}
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          >
            <option value="bueno">🟢 Bueno</option>
            <option value="regular">🟡 Regular</option>
            <option value="malo">🔴 Malo</option>
          </select>
        </div>
      </div>

      {/* Alerta de mantenimiento */}
      <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-700">
        <input
          type="checkbox"
          checked={localConfig.requiereMantenimiento}
          onChange={(e) => updateConfig({ requiereMantenimiento: e.target.checked })}
          className="w-4 h-4 text-orange-600"
        />
        <AlertTriangle className="w-4 h-4 text-orange-500" />
        <span className="text-sm">Requiere mantenimiento</span>
      </label>

      {/* Cables en el poste (con reservorios/gasas) */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium flex items-center gap-2">
            <Cable className="w-4 h-4" />
            Cables y Reservorios/Gasas
          </h4>
          <button
            type="button"
            onClick={addCable}
            className="text-sm px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Agregar Cable
          </button>
        </div>

        {localConfig.cables.length === 0 ? (
          <div className="text-center py-6 text-gray-500 border-2 border-dashed rounded-lg">
            <Cable className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Sin cables registrados</p>
            <p className="text-xs">Agrega los cables que pasan por este poste</p>
          </div>
        ) : (
          <div className="space-y-3">
            {localConfig.cables.map((cable) => (
              <div 
                key={cable.id} 
                className={`border rounded-lg p-4 ${cable.tieneGasa ? 'bg-green-50 dark:bg-green-900/10 border-green-300 dark:border-green-700' : 'dark:border-dark-600'}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500">Cable</label>
                      {cablesDisponibles.length > 0 ? (
                        <select
                          value={cable.cableId || ''}
                          onChange={(e) => {
                            const selected = cablesDisponibles.find(c => c.id === e.target.value)
                            updateCable(cable.id, { 
                              cableId: e.target.value,
                              cableNombre: selected?.nombre || ''
                            })
                          }}
                          className="w-full mt-1 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                        >
                          <option value="">Seleccionar...</option>
                          {cablesDisponibles.map(c => (
                            <option key={c.id} value={c.id}>{c.nombre}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={cable.cableNombre}
                          onChange={(e) => updateCable(cable.id, { cableNombre: e.target.value })}
                          placeholder="Nombre del cable"
                          className="w-full mt-1 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                        />
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Tipo Instalación</label>
                      <select
                        value={cable.tipoInstalacion}
                        onChange={(e) => updateCable(cable.id, { tipoInstalacion: e.target.value as TipoInstalacion })}
                        className="w-full mt-1 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                      >
                        <option value="aereo">Aéreo (paso)</option>
                        <option value="bajante">Bajante</option>
                        <option value="subida">Subida</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Altura (m)</label>
                      <input
                        type="number"
                        value={cable.altura}
                        onChange={(e) => updateCable(cable.id, { altura: parseFloat(e.target.value) })}
                        step="0.5"
                        min="0"
                        max={localConfig.altura}
                        className="w-full mt-1 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Metros en poste</label>
                      <input
                        type="number"
                        value={cable.metrosEnPoste}
                        onChange={(e) => updateCable(cable.id, { metrosEnPoste: parseFloat(e.target.value) })}
                        step="0.1"
                        min="0"
                        className="w-full mt-1 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeCable(cable.id)}
                    className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Sección de Reservorio/Gasa */}
                <div className="mt-3 pt-3 border-t dark:border-dark-600">
                  <label className="flex items-center gap-2 mb-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={cable.tieneGasa}
                      onChange={(e) => updateCable(cable.id, { 
                        tieneGasa: e.target.checked,
                        reserva: e.target.checked ? (cable.reserva || 3) : 0
                      })}
                      className="w-4 h-4 text-green-600"
                    />
                    <Box className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium">Dejar reservorio/gasa en este poste</span>
                  </label>

                  {cable.tieneGasa && (
                    <div className="ml-6 grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-500">Metros de reserva</label>
                        <input
                          type="number"
                          value={cable.reserva}
                          onChange={(e) => updateCable(cable.id, { reserva: parseFloat(e.target.value) })}
                          step="0.5"
                          min="0.5"
                          max="30"
                          className="w-full mt-1 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Notas</label>
                        <input
                          type="text"
                          value={cable.notas || ''}
                          onChange={(e) => updateCable(cable.id, { notas: e.target.value })}
                          placeholder="Ej: Para futura NAP"
                          className="w-full mt-1 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Resumen de reservorios en cables */}
        {localConfig.cables.some(c => c.tieneGasa) && (
          <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
            <h5 className="font-medium text-green-800 dark:text-green-400 mb-2 flex items-center gap-2">
              <Box className="w-4 h-4" />
              Gasas en cables
            </h5>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600 dark:text-gray-400">Cables con gasa:</span>
                <span className="ml-2 font-medium">{cablesConGasa}</span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Metros en cables:</span>
                <span className="ml-2 font-medium">
                  {localConfig.cables.reduce((acc, c) => acc + c.reserva, 0).toFixed(1)} m
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Reservas de fibra independientes */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium flex items-center gap-2">
            <Archive className="w-4 h-4" />
            Reservas de Fibra (Slack)
          </h4>
          <button
            type="button"
            onClick={addReserva}
            className="text-sm px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Nueva Reserva
          </button>
        </div>

        <p className="text-xs text-gray-500 mb-3">
          Gestiona reservas de fibra adicionales para mantenimiento, extensiones o emergencias.
        </p>

        {localConfig.reservas.length === 0 ? (
          <div className="text-center py-4 text-gray-500 border-2 border-dashed rounded-lg">
            <Archive className="w-6 h-6 mx-auto mb-1 opacity-50" />
            <p className="text-sm">Sin reservas adicionales</p>
          </div>
        ) : (
          <div className="space-y-3">
            {localConfig.reservas.map((reserva) => (
              <div 
                key={reserva.id}
                className="border rounded-lg p-4 bg-green-50 dark:bg-green-900/10 border-green-300 dark:border-green-700"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-gray-500">Cable</label>
                      {cablesDisponibles.length > 0 ? (
                        <select
                          value={reserva.cableId || ''}
                          onChange={(e) => {
                            const selected = cablesDisponibles.find(c => c.id === e.target.value)
                            updateReserva(reserva.id, { 
                              cableId: e.target.value,
                              cableNombre: selected?.nombre || ''
                            })
                          }}
                          className="w-full mt-1 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                        >
                          <option value="">Seleccionar...</option>
                          {cablesDisponibles.map(c => (
                            <option key={c.id} value={c.id}>{c.nombre}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={reserva.cableNombre}
                          onChange={(e) => updateReserva(reserva.id, { cableNombre: e.target.value })}
                          placeholder="Nombre del cable"
                          className="w-full mt-1 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                        />
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Metros de reserva</label>
                      <input
                        type="number"
                        value={reserva.metrosReserva}
                        onChange={(e) => updateReserva(reserva.id, { metrosReserva: parseFloat(e.target.value) })}
                        step="0.5"
                        min="0.5"
                        max="50"
                        className="w-full mt-1 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Tipo de reserva</label>
                      <select
                        value={reserva.tipoReserva}
                        onChange={(e) => updateReserva(reserva.id, { tipoReserva: e.target.value as TipoReserva })}
                        className="w-full mt-1 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                      >
                        <option value="bobina">🔄 Bobina</option>
                        <option value="figura8">∞ Figura 8</option>
                        <option value="serpentin">〰️ Serpentín</option>
                        <option value="caja">📦 En caja/cierre</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Propósito</label>
                      <select
                        value={reserva.proposito}
                        onChange={(e) => updateReserva(reserva.id, { proposito: e.target.value as PropositoReserva })}
                        className="w-full mt-1 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                      >
                        <option value="mantenimiento">🔧 Mantenimiento</option>
                        <option value="extension">📈 Extensión futura</option>
                        <option value="reubicacion">🔄 Reubicación</option>
                        <option value="emergencia">🚨 Emergencia</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Altura instalación (m)</label>
                      <input
                        type="number"
                        value={reserva.alturaInstalacion}
                        onChange={(e) => updateReserva(reserva.id, { alturaInstalacion: parseFloat(e.target.value) })}
                        step="0.5"
                        min="0"
                        max={localConfig.altura}
                        className="w-full mt-1 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Condición</label>
                      <select
                        value={reserva.condicion}
                        onChange={(e) => updateReserva(reserva.id, { condicion: e.target.value as 'buena' | 'regular' | 'mala' })}
                        className="w-full mt-1 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                      >
                        <option value="buena">🟢 Buena</option>
                        <option value="regular">🟡 Regular</option>
                        <option value="mala">🔴 Mala</option>
                      </select>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeReserva(reserva.id)}
                    className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500">Fecha instalación</label>
                    <input
                      type="date"
                      value={reserva.fechaInstalacion || ''}
                      onChange={(e) => updateReserva(reserva.id, { fechaInstalacion: e.target.value })}
                      className="w-full mt-1 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Notas</label>
                    <input
                      type="text"
                      value={reserva.notas || ''}
                      onChange={(e) => updateReserva(reserva.id, { notas: e.target.value })}
                      placeholder="Observaciones..."
                      className="w-full mt-1 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Resumen total de reservas */}
      {(localConfig.cables.some(c => c.tieneGasa) || localConfig.reservas.length > 0) && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <h5 className="font-medium text-blue-800 dark:text-blue-400 mb-2 flex items-center gap-2">
            <Package className="w-4 h-4" />
            Resumen Total de Reservas en Poste
          </h5>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">En cables (gasas):</span>
              <span className="ml-2 font-medium">
                {localConfig.cables.reduce((acc, c) => acc + c.reserva, 0).toFixed(1)} m
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Reservas independientes:</span>
              <span className="ml-2 font-medium">
                {localConfig.reservas.reduce((acc, r) => acc + r.metrosReserva, 0).toFixed(1)} m
              </span>
            </div>
            <div className="font-bold text-blue-700 dark:text-blue-300">
              <span>TOTAL:</span>
              <span className="ml-2">{totalReserva.toFixed(1)} m</span>
            </div>
          </div>
          {localConfig.reservas.some(r => r.condicion === 'mala') && (
            <div className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-700 flex items-center gap-2 text-sm text-orange-600">
              <AlertTriangle className="w-4 h-4" />
              <span>Hay reservas en mala condición que requieren atención</span>
            </div>
          )}
        </div>
      )}

      {/* Herrajes */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium flex items-center gap-2">
            <Anchor className="w-4 h-4" />
            Herrajes
          </h4>
          <button
            type="button"
            onClick={addHerraje}
            className="text-sm px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Agregar Herraje
          </button>
        </div>

        {localConfig.herrajes.length > 0 && (
          <div className="space-y-2">
            {localConfig.herrajes.map((herraje) => (
              <div key={herraje.id} className="flex items-center gap-3 p-2 border rounded-lg dark:border-dark-600">
                <select
                  value={herraje.tipo}
                  onChange={(e) => updateHerraje(herraje.id, { tipo: e.target.value as TipoHerraje })}
                  className="px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                >
                  <option value="grapa">Grapa</option>
                  <option value="abrazadera">Abrazadera</option>
                  <option value="gancho">Gancho J</option>
                  <option value="fleje">Fleje</option>
                  <option value="preformado">Preformado</option>
                </select>
                <input
                  type="number"
                  value={herraje.cantidad}
                  onChange={(e) => updateHerraje(herraje.id, { cantidad: parseInt(e.target.value) })}
                  min="1"
                  className="w-16 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                />
                <input
                  type="text"
                  value={herraje.material}
                  onChange={(e) => updateHerraje(herraje.id, { material: e.target.value })}
                  placeholder="Material"
                  className="flex-1 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                />
                <button
                  type="button"
                  onClick={() => removeHerraje(herraje.id)}
                  className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tensores */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium">Tensores/Riendas</h4>
          <button
            type="button"
            onClick={addTensor}
            className="text-sm px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Agregar Tensor
          </button>
        </div>

        {localConfig.tensores.length > 0 && (
          <div className="space-y-2">
            {localConfig.tensores.map((tensor) => (
              <div key={tensor.id} className="flex items-center gap-3 p-2 border rounded-lg dark:border-dark-600">
                <select
                  value={tensor.tipo}
                  onChange={(e) => updateTensor(tensor.id, { tipo: e.target.value as TipoTensor })}
                  className="px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                >
                  <option value="tensor">Tensor</option>
                  <option value="rienda">Rienda</option>
                  <option value="viento">Viento</option>
                  <option value="ancla">Ancla</option>
                </select>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    value={tensor.longitud}
                    onChange={(e) => updateTensor(tensor.id, { longitud: parseFloat(e.target.value) })}
                    step="0.5"
                    min="0"
                    className="w-20 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                  />
                  <span className="text-sm text-gray-500">m</span>
                </div>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    value={tensor.angulo || ''}
                    onChange={(e) => updateTensor(tensor.id, { angulo: parseFloat(e.target.value) || undefined })}
                    placeholder="Ángulo"
                    min="0"
                    max="90"
                    className="w-20 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                  />
                  <span className="text-sm text-gray-500">°</span>
                </div>
                <button
                  type="button"
                  onClick={() => removeTensor(tensor.id)}
                  className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Observaciones */}
      <div>
        <label className="block text-sm font-medium mb-1">Observaciones</label>
        <textarea
          value={localConfig.observaciones || ''}
          onChange={(e) => updateConfig({ observaciones: e.target.value })}
          rows={2}
          placeholder="Notas adicionales sobre el poste..."
          className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
        />
      </div>
    </div>
  )
}
